import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { BackButton } from './BackButton';

interface Props {
  title: string;
  subtitle?: ReactNode;
  eyebrow?: ReactNode;
  actions?: ReactNode;
  /** When provided, renders a left-aligned "Back" link above the title. */
  onBack?: () => void;
  /** Label for the back link (default "Back"). */
  backLabel?: string;
  className?: string;
}

export function PageHeader({
  title,
  subtitle,
  eyebrow,
  actions,
  onBack,
  backLabel = 'Back',
  className,
}: Props) {
  return (
    <div className={cn('mb-8', className)}>
      {onBack && (
        <BackButton onClick={onBack} label={backLabel} />
      )}
      {/* flex-wrap so a wide action group drops onto its own line on a narrow screen instead
          of overflowing off the right edge. A page carrying three actions measured 518px in a
          390px viewport and its primary button sat entirely outside the viewport, unclickable.
          No-op whenever the row already fits, so wider pages are unchanged. */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        {/* basis-full below sm: the title takes the whole first line and the action group wraps
            underneath it. Without it the group's shrink-0 kept ~340px on a 390px screen and the
            title was squeezed into a column one word wide ("My / Matters", subtitle one word a
            line).

            From sm up it asks for 20rem rather than 0. basis-0 let the title shrink to nothing
            so the actions never had to wrap: on an iPad a three-action group took 518px of the
            720px available and left the subtitle a 186px column reading six words deep. With a
            20rem floor the flex line simply runs out of room and the ACTIONS wrap instead, which
            is the thing that should give way. No breakpoint picks the moment — the content does,
            so a one-button header still sits beside its title at the same width it always did. */}
        <div className="min-w-0 flex-1 basis-full sm:basis-80">
          {eyebrow && (
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
              {eyebrow}
            </div>
          )}
          <h1 className="text-2xl font-semibold leading-tight tracking-[-0.02em] text-foreground">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-1.5 text-[12.5px] leading-relaxed text-muted-foreground">{subtitle}</p>
          )}
        </div>
        {actions && (
          // Wraps internally too, so a group that is itself wider than the viewport stacks
          // rather than running off the edge.
          // max-w-full is what actually makes the wrap bite: shrink-0 alone lets the group
          // size to its content (518px) and sit outside a 390px viewport regardless.
          //
          // basis-full below sm, mirroring the title block above: the group gets the whole line,
          // so an action row that wants to divide the width between its buttons has a width to
          // divide. shrink-0 alone sized the group to its content, which left `flex-1` buttons
          // inside it nothing to stretch into and squeezed their labels to "Copy li…".
          // justify-end keeps every other page's actions right-aligned exactly as before.
          //
          // Below sm the content decides, not a breakpoint. Each action keeps its NATURAL width
          // and `grow` hands it only the slack, so a pair that fits — "List view" + "New matter"
          // — shares one line, while a pair that does not — "Pipeline" + "New personal matter",
          // ~300px against a 298px phone — wraps and each takes a full line.
          //
          // `flex-1` was tried first and is wrong here: it forces equal halves regardless of the
          // label, which squeezed "New personal matter" into two lines inside its own button.
          // Below `sm` the group is a TWO-COLUMN GRID rather than a wrapping flex row.
          // `grow` shared out the slack in proportion to each label's natural width, so a long
          // run of actions landed in ragged rows of different sizes — the matter page's seven
          // (Pin, Edit, Re-run conflict check, Schedule hearing, Close matter, Archive, Log
          // time) came out as 2 + 1 + 2 + 2 at jumbled widths. A grid gives every action the
          // same cell.
          //
          // `last-child:nth-child(odd)` spans the final button across both columns when the
          // count is odd. That keeps a ONE-action header full width exactly as it was, and it
          // puts the primary action — which is conventionally last — on its own full-width row.
          // From `sm` up the group is the flex row it has always been — right-aligned, wrapping
          // only if it must. That is correct for the one to three actions most headers carry.
          //
          // A CROWDED header is the exception. The matter page has seven, and between 640px and
          // 1280px they wrapped into two rows of SIX DIFFERENT WIDTHS (85, 90, 119, 123, 183,
          // 211) — the rows read as an accident rather than a toolbar. `:has(>:nth-child(5))`
          // asks the group itself whether it is carrying five or more actions and, only then,
          // lays them out as equal cells. Four columns keeps the same row count it already had;
          // it just makes the rows line up.
          //
          // At `xl` all seven fit on one line, so the plain flex row is restored.
          //
          // `sm:gap-3` from `gap-2`: at 8px a pair of header buttons read as one welded control
          // rather than two. Only from sm — below it the actions are stacked full-width rows and
          // 8px is the right rhythm for a phone.
          <div className="grid max-w-full shrink-0 grow basis-full grid-cols-2 items-stretch gap-2 pt-0.5 sm:gap-3 [&>*:last-child:nth-child(odd)]:col-span-2 sm:ml-auto sm:flex sm:basis-auto sm:flex-wrap sm:items-center sm:justify-end sm:[&>*]:grow-0 sm:[&:has(>:nth-child(5))]:grid sm:[&:has(>:nth-child(5))]:grid-cols-4 xl:[&:has(>:nth-child(5))]:flex">{actions}</div>
        )}
      </div>
    </div>
  );
}
